import { motion } from 'framer-motion';

import { trackEvent } from '../../../util/analytics';
import { DEMO_BOOKING_URL } from '../../../data/demoBooking';

/**
 * DemoConversionSection — final CTA band.
 *
 * A single frosted "Book a demo" card over the global tree scene. The
 * alternative-contact options that used to sit beside it now live in the
 * footer (see Footer's ContactOptions).
 */
export function DemoConversionSection() {
  return (
    <section
      id='demo'
      className='relative overflow-hidden py-10 md:py-14 bg-zinc-950/70'
    >
      <div className='tc-container relative z-10'>
        <div className='max-w-xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className='p-7 md:p-9 rounded-2xl bg-zinc-950/60 backdrop-blur-md border border-white/15'
          >
            <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 bg-jade-deep/30 text-jade-soft border border-jade-deep/40'>
              <span className='w-1.5 h-1.5 rounded-full bg-jade animate-pulse' />
              Spots available this week
            </div>

            <h2 className='text-2xl md:text-3xl font-extrabold leading-tight mb-3 font-display text-white [text-wrap:balance]'>
              See TimberCloud running on your products.
            </h2>

            <p className='text-sm md:text-base mb-6 text-zinc-200 leading-relaxed'>
              30 minutes with your actual catalog &mdash; end to end, order to
              floor. No slides, no pitch deck.
            </p>

            <a
              href={DEMO_BOOKING_URL}
              target='_blank'
              rel='noopener noreferrer'
              // Click, NOT a booking — this CTA leaves the site for
              // calendly.com. See the note in iwf/IwfBooth.jsx. Bookings that
              // start here land in GA4 only via the webhook's server-side
              // path; `demo_requested` fires solely from the /demo embed.
              onClick={() =>
                trackEvent('demo_cta_click', {
                  event_source: 'site',
                  cta_location: 'demo_conversion_section',
                  page_path: window.location.pathname,
                })
              }
              className='group block w-full px-6 py-3.5 bg-gradient-to-r from-jade-cta to-jade-cta-deep text-white rounded-xl text-sm font-semibold text-center shadow-lg shadow-jade-deep/30 hover:shadow-jade-deep/50 hover:scale-[1.02] transition-all duration-300'
            >
              <span className='flex items-center justify-center gap-2'>
                Book a demo
                <svg
                  className='w-4 h-4 group-hover:translate-x-1 transition-transform'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <path
                    d='M5 12h14m-7-7l7 7-7 7'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
            </a>

            <a
              href='/#'
              className='mt-3 block text-center text-sm font-medium text-jade-soft hover:text-jade'
            >
              Or see pricing &rarr;
            </a>

            <div className='mt-4 flex items-center justify-center gap-3 text-xs text-zinc-400'>
              <span>30 min</span>
              <span className='w-1 h-1 rounded-full bg-zinc-500' />
              <span>1-on-1</span>
              <span className='w-1 h-1 rounded-full bg-zinc-500' />
              <span>No obligation</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default DemoConversionSection;
